/**
 * Invited suppliers and where each one is with their quote.
 *
 * Shown on the customer's Responses tab so a sent RFQ with no submitted
 * quotes still tells the buyer who was invited and that they are awaiting
 * a response, rather than an empty "nothing to compare" panel.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Building, Clock, CheckCircle, Trophy, XCircle } from 'lucide-react';
import type { SupplierResponseListItem, SupplierResponseStatus } from '../../lib/api';

interface InvitedSuppliersListProps {
  responses: SupplierResponseListItem[];
  title?: string;
  description?: string;
  formatDate?: (date: string) => string;
}

const STATUS_META: Record<SupplierResponseStatus, { label: string; className: string; icon: typeof Clock }> = {
  pending: { label: 'Awaiting response', className: 'bg-amber-100 text-amber-800', icon: Clock },
  in_progress: { label: 'Drafting response', className: 'bg-blue-100 text-blue-800', icon: Clock },
  submitted: { label: 'Submitted', className: 'bg-green-100 text-green-800', icon: CheckCircle },
  won: { label: 'Awarded', className: 'bg-green-600 text-white', icon: Trophy },
  lost: { label: 'Not awarded', className: 'bg-gray-200 text-gray-700', icon: XCircle },
  // Reads differently from "Not awarded" on purpose: the buyer needs to see at a
  // glance which invitations were declined by silence rather than outbid.
  no_bid: { label: 'No bid', className: 'bg-gray-100 text-gray-600', icon: XCircle },
};

export default function InvitedSuppliersList({
  responses,
  title = 'Invited Suppliers',
  description = 'Suppliers this RFQ was sent to and the status of their response',
  formatDate,
}: InvitedSuppliersListProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{title} ({responses.length})</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {responses.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No suppliers have been invited yet.</p>
        ) : (
          <ul className="divide-y">
            {responses.map((r) => {
              const meta = STATUS_META[r.response_status] ?? STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="flex items-center gap-2 font-medium">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    {r.supplier_name}
                  </span>
                  <span className="flex items-center gap-3 text-xs text-muted-foreground">
                    {r.submitted_date && formatDate && <span>Submitted {formatDate(r.submitted_date)}</span>}
                    <Badge className={meta.className}>
                      <Icon className="h-3 w-3 mr-1" />
                      {meta.label}
                    </Badge>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
